import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Tag, GripVertical, Check, Palette, LayoutGrid } from 'lucide-react';
import { databaseService, ID } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';

const CATEGORY_COLLECTION = 'shop_categories';

export default function CategoryManagement() {
  const { showToast } = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: '#4C1D95' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const result = await databaseService.listDocuments(CATEGORY_COLLECTION);
      if (result.success) {
        // Sort by order if available, otherwise by name
        const sorted = result.data.documents.sort((a, b) => (a.order || 0) - (b.order || 0));
        setCategories(sorted);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showToast('Category name is required', 'error');
      return;
    }

    setSaving(true);
    const categoryData = {
      name: formData.name.trim(),
      color: formData.color,
      order: editingCategory ? editingCategory.order : categories.length,
    };

    try {
      if (editingCategory) {
        const result = await databaseService.updateDocument(
          CATEGORY_COLLECTION, editingCategory.$id, categoryData
        );
        if (result.success) showToast('Category updated successfully', 'success');
      } else {
        const result = await databaseService.createDocument(
          CATEGORY_COLLECTION, categoryData
        );
        if (result.success) showToast('Category created successfully', 'success');
      }
      setShowModal(false);
      resetForm();
      loadCategories();
    } catch (error) {
      showToast('Failed to save category', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Delete "${category.name}"? Products with this category will become uncategorized.`)) return;
    
    try {
      const result = await databaseService.deleteDocument(CATEGORY_COLLECTION, category.$id);
      if (result.success) {
        showToast('Category deleted', 'success');
        loadCategories();
      }
    } catch (error) {
      showToast('Failed to delete category', 'error');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, color: category.color || '#4C1D95' });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ name: '', color: '#4C1D95' });
    setEditingCategory(null);
  };

  const colorPresets = [
    '#4C1D95', '#1E8479', '#ef4444', '#f97316', '#eab308', 
    '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280', '#111827'
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#4C1D95]">Shop Categories</h1>
          <p className="text-gray-500 mt-1">Organize your products with colorful tags</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#4C1D95] text-white rounded-xl hover:bg-[#3b1575] transition-all shadow-md hover:shadow-lg active:scale-95 font-medium"
        >
          <Plus className="w-5 h-5" />
          Add Category
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#4C1D95]"></div>
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-gray-200">
          <div className="w-20 h-20 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Tag className="w-10 h-10 text-[#4C1D95]" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">No categories yet</h3>
          <p className="text-gray-500 mb-8 max-w-sm mx-auto">Create categories to help customers find what they're looking for.</p>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#4C1D95] text-white rounded-xl hover:bg-[#3b1575] transition-colors font-medium"
          >
           <Plus className="w-5 h-5" />
           Create First Category
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {categories.map((category) => (
            <div
              key={category.$id}
              className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
            >
              {/* Card Header with Color */}
              <div 
                className="h-24 relative overflow-hidden flex items-center justify-center"
                style={{ backgroundColor: category.color || '#4C1D95' }}
              >
                <div className="absolute inset-0 bg-black/10"></div>
                {/* Decorative circles */}
                <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white/10 blur-xl"></div>
                <div className="absolute -left-4 -bottom-4 w-16 h-16 rounded-full bg-white/10 blur-xl"></div>
                
                <Tag className="w-10 h-10 text-white opacity-90 drop-shadow-md transform group-hover:scale-110 transition-transform duration-500" />
              </div>

              {/* Card Body */}
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-lg font-bold text-gray-800 group-hover:text-[#4C1D95] transition-colors mb-4 break-words">
                  {category.name}
                </h3>
                
                <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-100">
                   <div className="flex gap-2">
                     <div className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center bg-gray-50 text-[10px] font-bold text-gray-600 cursor-grab active:cursor-grabbing" title="Drag to reorder (coming soon)">
                        <GripVertical className="w-3 h-3" />
                     </div>
                   </div>
                   <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(category)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                   </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden scale-100 animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {editingCategory ? 'Edit Category' : 'Create Category'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">Define properties for this product category</p>
              </div>
              <button 
                onClick={() => setShowModal(false)} 
                className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Name Input */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Category Name <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                    placeholder="e.g., Digital Assets, Prints, Hardware"
                    required
                    autoFocus
                  />
                  <LayoutGrid className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
              </div>

              {/* Color Selection */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 flex items-center justify-between">
                  <span>Theme Color</span>
                  <span className="text-xs font-normal text-gray-500" style={{ color: formData.color }}>Preview Text</span>
                </label>
                
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-4">
                  {/* Presets */}
                  <div className="flex flex-wrap gap-3">
                    {colorPresets.map(color => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, color }))}
                          className={`w-8 h-8 rounded-full shadow-sm transition-all hover:scale-110 flex items-center justify-center ${
                             formData.color === color ? 'ring-2 ring-offset-2 ring-[#4C1D95] scale-110' : ''
                          }`}
                          style={{ backgroundColor: color }}
                          title={color}
                        >
                          {formData.color === color && <Check className="w-4 h-4 text-white" />}
                        </button>
                    ))}
                  </div>
                  
                  {/* Custom Picker */}
                  <div className="flex items-center gap-3 pt-2 border-t border-gray-200/50">
                    <div className="relative overflow-hidden w-10 h-10 rounded-lg shadow-sm border border-gray-200 ring-1 ring-black/5 cursor-pointer">
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                        className="absolute inset-0 w-[150%] h-[150%] -top-[25%] -left-[25%] cursor-pointer p-0 border-0"
                      />
                    </div>
                    <div className="flex-1 relative">
                       <input
                        type="text"
                        value={formData.color}
                        onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg uppercase font-mono text-gray-600 focus:border-[#4C1D95] focus:ring-1 focus:ring-[#4C1D95] outline-none"
                        placeholder="#000000"
                      />
                      <Palette className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 bg-[#4C1D95] text-white rounded-xl hover:bg-[#3b1575] font-bold shadow-lg shadow-purple-900/10 transition-all flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {editingCategory ? 'Update Changes' : 'Create Category'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
